/**
 * Notification Center Component
 * Dropdown panel displaying notifications with actions
 */

import { useState, forwardRef } from 'react';
import {
  CheckCircle,
  X,
  Bell,
  Mail,
  AlertCircle,
  MessageSquare,
  Workflow,
  UserPlus,
  CheckCheck,
  Trash2,
  ExternalLink,
  Phone,
  Calendar,
  FileText,
  ArrowRight,
  Video,
  ClipboardList,
  Target,
  Share2,
  Archive,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { Button } from './button';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { BellPushStatus } from '@/components/notifications/BellPushStatus';

interface Notification {
  id: number;
  user_id: number;
  organization_id: number | null;
  notification_type: string;
  title: string;
  message: string;
  link: string | null;
  icon: string | null;
  priority: string;
  read: boolean;
  read_at: string | null;
  archived: boolean;
  archived_at: string | null;
  related_member_id: number | null;
  related_workflow_id: number | null;
  related_task_id: number | null;
  related_referral_id: number | null;
  related_enrollment_id: number | null;
  related_activity_id: string | null;
  created_at: string;
  expires_at: string | null;
}

interface NotificationSummary {
  unread_count: number;
  read_count?: number | null;
  urgent_unread_count: number;
  total_count?: number | null;
  last_notification_at: string | null;
  recent_unread: Notification[];
}

interface NotificationCenterProps {
  summary: NotificationSummary | null;
  onClose: () => void;
  onRefresh: () => void;
  position: { top: number; left: number };
  onNavigate?: (tab: string) => void;
}

export const NotificationCenter = forwardRef<HTMLDivElement, NotificationCenterProps>(
  ({ summary, onClose, onRefresh, position, onNavigate }, ref) => {
    const { t } = useAppTranslation();
    const { fmt } = useFormattedDate();
    const [markingAllRead, setMarkingAllRead] = useState(false);

  // Get icon component based on notification type or custom icon
  const getIcon = (notification: Notification) => {
    const iconName = notification.icon || notification.notification_type;
    const className = `h-5 w-5 ${
      notification.priority === 'urgent' ? 'text-red-500' :
      notification.priority === 'high' ? 'text-orange-500' :
      'text-blue-500'
    }`;

    switch (iconName) {
      case 'CheckCircle':
      case 'workflow_completion':
        return <CheckCircle className={className} />;
      case 'Mail':
      case 'communications':
        return <Mail className={className} />;
      case 'MessageSquare':
        return <MessageSquare className={className} />;
      case 'Workflow':
      case 'workflow':
        return <Workflow className={className} />;
      case 'UserPlus':
      case 'enrollments':
        return <UserPlus className={className} />;
      case 'AlertCircle':
      case 'system':
        return <AlertCircle className={className} />;
      case 'Phone':
        return <Phone className={className} />;
      case 'Calendar':
        return <Calendar className={className} />;
      case 'FileText':
      case 'document':
        return <FileText className={className} />;
      case 'Video':
      case 'telehealth':
        return <Video className={className} />;
      case 'ClipboardList':
      case 'activity':
      case 'task':
        return <ClipboardList className={className} />;
      case 'ArrowRight':
      case 'stage_change':
        return <ArrowRight className={className} />;
      case 'Target':
        return <Target className={className} />;
      case 'referral':
        return <Share2 className={className} />;
      default:
        return <Bell className={className} />;
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: number) => {
    try {
      await apiClient.patch(`/api/notifications/${notificationId}/read`);
      onRefresh();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  // Archive notification (hide from list, sync email for communications)
  const archiveNotification = async (notificationId: number, isCommunication: boolean = false) => {
    try {
      const params = isCommunication ? { params: { sync_email: true } } : {};
      await apiClient.patch(`/api/notifications/${notificationId}/archive`, undefined, params);
      toast.success('Notification archived');
      onRefresh();
    } catch (error) {
      console.error('Error archiving notification:', error);
      toast.error('Failed to archive notification');
    }
  };

  // Delete notification (and sync with email server for communications)
  const deleteNotification = async (notificationId: number, isCommunication: boolean = false) => {
    try {
      const params = isCommunication ? { params: { sync_email: true } } : {};
      await apiClient.delete(`/api/notifications/${notificationId}`, params);
      toast.success('Notification deleted');
      onRefresh();
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      setMarkingAllRead(true);
      await apiClient.patch('/api/notifications/mark-all-read');
      toast.success('All notifications marked as read');
      onRefresh();
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate to link if provided
    if (notification.link) {
      onClose();

      // Extract the tab/path from the link, separating query params
      let path = notification.link;
      try {
        const url = new URL(notification.link, window.location.origin);
        path = url.pathname + url.search;
      } catch {
        // If URL parsing fails, use the link as-is
      }

      // Strip leading slash to get the SPA tab name
      const cleanPath = path.replace(/^\//, '');
      const [basePath, queryString] = cleanPath.split('?');

      // Store query params so the target page can read them
      if (queryString) {
        const params = new URLSearchParams(queryString);
        const subTab = params.get('tab');
        if (subTab) {
          localStorage.setItem('navigate_sub_tab', subTab);
        }
        const step = params.get('step');
        if (step) {
          localStorage.setItem('navigate_enrollment_step', step);
        }
      }

      // Handle deep-link patterns that need ID extraction for SPA routing
      const enrollmentMatch = basePath.match(/^enrollments\/(\d+)$/);
      if (enrollmentMatch && onNavigate) {
        localStorage.setItem('selectedEnrollmentId', enrollmentMatch[1]);
        onNavigate('enrollments-view');
        return;
      }

      // Use SPA navigation if available, otherwise fall back to full page nav
      if (onNavigate) {
        onNavigate(basePath);
      } else {
        window.location.href = path;
      }
    }
  };

  const recentNotifications = summary?.recent_unread || [];
  const unreadCount = summary?.unread_count || 0;

  return (
    <div
      ref={ref}
      className="fixed w-96 rounded-lg shadow-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 max-h-[600px] flex flex-col"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10001,
        pointerEvents: 'auto'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('notifications.title', 'Notifications')}
          </h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              disabled={markingAllRead}
              className="text-xs"
              title={t('ui.mark_all_as_read', 'Mark all as read')}
            >
              <CheckCheck className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <BellPushStatus />

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {recentNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Bell className="h-12 w-12 text-gray-400 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              {t('notifications.noNewNotifications', 'No new notifications')}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1">
              {t('notifications.allCaughtUp', "You're all caught up!")}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {recentNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                  !notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                }`}
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getIcon(notification)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`cursor-pointer ${
                        notification.link ? 'hover:underline' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {notification.title}
                        </p>
                        {notification.priority === 'urgent' && (
                          <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded">
                            {t('ui.urgent', 'Urgent')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-xs text-gray-500 dark:text-gray-500 dark:text-gray-400">
                          {fmt.custom(notification.created_at, 'MMM d, h:mm a')}
                        </p>
                        {notification.link && (
                          <ExternalLink className="h-3 w-3 text-gray-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      {!notification.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium flex items-center gap-1.5 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        >
                          <CheckCircle className="h-4 w-4" />
                          {t('notifications.markAsRead', 'Mark as read')}
                        </button>
                      )}
                      {notification.notification_type === 'communication' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            archiveNotification(notification.id, true);
                          }}
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-amber-50 dark:hover:bg-amber-900/30"
                        >
                          <Archive className="h-4 w-4" />
                          {t('buttons.archive', 'Archive')}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id, notification.notification_type === 'communication');
                        }}
                        className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('buttons.delete', 'Delete')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700 text-center">
        <button
          onClick={() => {
            if (onNavigate) {
              localStorage.setItem('preferences_default_tab', 'notifications');
              onNavigate('user-preferences');
            } else {
              window.location.href = '/notifications';
            }
            onClose();
          }}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
        >
          {t('notifications.viewAllNotifications', 'View all {{count}} notifications').replace('{{count}}', String(unreadCount))}
        </button>
      </div>
    </div>
  );
});

NotificationCenter.displayName = 'NotificationCenter';
